/**
 * Chore Status Transition Hook
 * Centralized logic for managing chore status progression
 */
import { useState, useCallback } from 'react'
import { apiClient } from '../utils/api'
import { ASSIGNMENT_STATUS, AssignmentStatus } from '../utils/assignmentUtils'

export interface StatusStep {
  value: AssignmentStatus | 'open'
  label: string
  shortLabel: string
  color: string
  bgColor: string
  textColor: string
}

// Status progression order (Open is only for bonus chores)
export const STATUS_ORDER: AssignmentStatus[] = [
  'pending',
  'assigned',
  'in_progress',
  'completed',
  'verified'
]

// Full status steps including 'open' for bonus chores
export const STATUS_STEPS: StatusStep[] = [
  { value: 'open', label: 'Open', shortLabel: 'Open', color: 'purple', bgColor: 'bg-purple-500', textColor: 'text-purple-600' },
  { value: 'pending', label: 'Assigned', shortLabel: 'Assigned', color: 'gray', bgColor: 'bg-gray-500', textColor: 'text-gray-600' },
  { value: 'assigned', label: 'Assigned', shortLabel: 'Assigned', color: 'blue', bgColor: 'bg-blue-500', textColor: 'text-blue-600' },
  { value: 'in_progress', label: 'In Progress', shortLabel: 'Started', color: 'yellow', bgColor: 'bg-yellow-500', textColor: 'text-yellow-600' },
  { value: 'completed', label: 'Completed', shortLabel: 'Done', color: 'orange', bgColor: 'bg-orange-500', textColor: 'text-orange-600' },
  { value: 'verified', label: 'Verified', shortLabel: 'Verified', color: 'green', bgColor: 'bg-green-500', textColor: 'text-green-600' }
]

// Simplified steps for display (merge pending/assigned, exclude open for regular chores)
export const DISPLAY_STEPS: StatusStep[] = [
  { value: 'assigned', label: 'Assigned', shortLabel: 'Assigned', color: 'blue', bgColor: 'bg-blue-500', textColor: 'text-blue-600' },
  { value: 'completed', label: 'Completed', shortLabel: 'Done', color: 'orange', bgColor: 'bg-orange-500', textColor: 'text-orange-600' },
  { value: 'verified', label: 'Verified', shortLabel: 'Verified', color: 'green', bgColor: 'bg-green-500', textColor: 'text-green-600' }
]

export function getStepForStatus(status: string): StatusStep {
  // Normalize status - pending and assigned show as 'Assigned' step
  const normalizedStatus = status === 'pending' || status === 'in_progress' ? 'assigned' : status
  return STATUS_STEPS.find(s => s.value === normalizedStatus) || DISPLAY_STEPS[0]
}

export function getStepIndex(status: string, includeVerified: boolean = true): number {
  const steps = includeVerified ? DISPLAY_STEPS : DISPLAY_STEPS.slice(0, -1)
  // Normalize: pending, assigned, in_progress all map to 'assigned' (index 0)
  if (status === 'pending' || status === 'assigned' || status === 'in_progress') {
    return 0
  }
  if (status === 'completed') {
    return 1
  }
  if (status === 'verified') {
    return includeVerified ? 2 : 1
  }
  return 0
}

export function canAdvanceStatus(
  currentStatus: string,
  isParent: boolean,
  verificationEnabled: boolean
): { canAdvance: boolean; nextStatus: string | null; requiresVerification: boolean } {
  // Determine what the next valid status is
  const normalizedStatus = currentStatus === 'in_progress' ? 'assigned' : currentStatus

  if (normalizedStatus === 'pending' || normalizedStatus === 'assigned') {
    // Anyone can mark as completed
    return { canAdvance: true, nextStatus: 'completed', requiresVerification: false }
  }

  if (normalizedStatus === 'completed') {
    if (!verificationEnabled) {
      // Auto-verify when verification is disabled
      return { canAdvance: true, nextStatus: 'verified', requiresVerification: false }
    }
    // Only parents can verify
    return {
      canAdvance: isParent,
      nextStatus: isParent ? 'verified' : null,
      requiresVerification: true
    }
  }

  // Already verified - no further advancement
  return { canAdvance: false, nextStatus: null, requiresVerification: false }
}

interface UseChoreStatusTransitionOptions {
  onSuccess?: (newStatus: string, data: any) => void
  onError?: (error: string) => void
}

interface TransitionResult {
  success: boolean
  newStatus?: string
  pointsAwarded?: number
  error?: string
}

export function useChoreStatusTransition(options: UseChoreStatusTransitionOptions = {}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const advanceToCompleted = useCallback(async (
    assignmentId: string,
    completionNotes?: string
  ): Promise<TransitionResult> => {
    setLoading(true)
    setError(null)

    try {
      const result = await apiClient.completeAssignment(assignmentId, completionNotes)
      options.onSuccess?.('completed', result)
      return {
        success: true,
        newStatus: result.requires_verification ? 'completed' : 'verified',
        pointsAwarded: result.points_earned
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to complete assignment'
      setError(errorMsg)
      options.onError?.(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setLoading(false)
    }
  }, [options])

  const advanceToVerified = useCallback(async (
    assignmentId: string,
    pointsToAward: number,
    verificationNotes?: string
  ): Promise<TransitionResult> => {
    setLoading(true)
    setError(null)

    try {
      const result = await apiClient.verifyAssignment(assignmentId, {
        approved: true,
        points_awarded: pointsToAward,
        verification_notes: verificationNotes
      })
      options.onSuccess?.('verified', result)
      return {
        success: true,
        newStatus: 'verified',
        pointsAwarded: result.points_earned
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to verify assignment'
      setError(errorMsg)
      options.onError?.(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setLoading(false)
    }
  }, [options])

  const rejectToAssigned = useCallback(async (
    assignmentId: string,
    rejectionNotes?: string
  ): Promise<TransitionResult> => {
    setLoading(true)
    setError(null)

    try {
      const result = await apiClient.verifyAssignment(assignmentId, {
        approved: false,
        verification_notes: rejectionNotes
      })
      options.onSuccess?.('assigned', result)
      return { success: true, newStatus: 'assigned' }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to reject assignment'
      setError(errorMsg)
      options.onError?.(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setLoading(false)
    }
  }, [options])

  return {
    loading,
    error,
    advanceToCompleted,
    advanceToVerified,
    rejectToAssigned,
    // Utility exports
    getStepForStatus,
    getStepIndex,
    canAdvanceStatus,
    DISPLAY_STEPS,
    STATUS_STEPS
  }
}
